import { RARITY_TINT } from '../screens/profile/title-picker';
import { SCENE_FOR_RARITY } from '../screens/profile/title-scene-map';
import type { TitleRarity } from '../screens/profile/profile-api';

/**
 * One place that turns a title (label + rarity group) into the plate used everywhere a title is shown: the profile
 * card, the header badge, chat lines and the colony popup. The look comes from the `.title-plate.pt-plate` CSS (the
 * same material as that group's equip scene); this only supplies the right attributes.
 *
 * The server can send rarity groups the plate has no material for (ACHIEVEMENT, DAILY), or a newer one than this
 * build knows — those fall back to the plain MISSION plate rather than rendering an unstyled `undefined` one.
 */
export interface TitleBadgeInput {
  label: string;
  rarity?: string | null;
}

export function normalizeRarity(rarity: string | null | undefined): TitleRarity {
  return rarity && rarity in RARITY_TINT ? (rarity as TitleRarity) : 'MISSION';
}

export function titleBadgeAttrs(rarity: string | null | undefined): { rarity: TitleRarity; scene: string; tint: string } {
  const r = normalizeRarity(rarity);
  return { rarity: r, scene: SCENE_FOR_RARITY[r], tint: RARITY_TINT[r] };
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/** The badge markup. `extraClass` picks the size/placement variant (e.g. `title-badge--chat`). */
export function titleBadgeHtml(title: TitleBadgeInput, extraClass = ''): string {
  const a = titleBadgeAttrs(title.rarity);
  return `<span class="title-plate pt-plate title-badge ${extraClass}" style="--title-tint:${a.tint}" data-rarity="${a.rarity}" data-scene="${a.scene}" data-title-badge><span>${escapeHtml(title.label)}</span></span>`;
}
